import { useEffect, useRef } from 'react'

import Decimal from 'decimal.js'
import { UseFormReturn, useWatch } from 'react-hook-form'

import {
  DeliveryHourType,
  ProductSpreadType,
} from '@enechain/eposition-proto/types/v1/deal_pb'
import { stringUtils } from '@enechain/eposition-utils'

import { OrderSimulationSchema } from '~/pages/Simulation/schemas'

type Props = {
  formMethods: UseFormReturn<OrderSimulationSchema>
  index: number
}

export function useSyncSummaryOrder({ formMethods, index }: Props): void {
  const baseSummaryOrder = useWatch({
    control: formMethods.control,
    name: `orderPattern.${index}.baseSummaryOrder`,
  })

  const isSpreadTrade = useWatch({
    control: formMethods.control,
    name: `orderPattern.${index}.spreadType.isSpreadTrade`,
  })

  const spreadTypes = useWatch({
    control: formMethods.control,
    name: `orderPattern.${index}.spreadType.spreadType`,
  })

  const baseUnitPricePerKwh = useWatch({
    control: formMethods.control,
    name: `orderPattern.${index}.baseSummaryOrder.unitPricePerKwh`,
  })

  const diffUnitPrice = useWatch({
    control: formMethods.control,
    name: `orderPattern.${index}.spreadType.diffUnitPrice`,
  })
  const { setValue, getValues } = formMethods

  useEffect(() => {
    if (isSpreadTrade === false) {
      setValue(`orderPattern.${index}.exchangeSummaryOrder`, undefined)
      return
    }

    if (spreadTypes === undefined) {
      setValue(`orderPattern.${index}.exchangeSummaryOrder`, undefined)
      return
    }

    const currentBase = getValues(`orderPattern.${index}.baseSummaryOrder`)
    const currentExchange = getValues(
      `orderPattern.${index}.exchangeSummaryOrder`,
    )

    if (spreadTypes.length === 0 || currentExchange === undefined) {
      setValue(`orderPattern.${index}.exchangeSummaryOrder`, {
        ...currentBase,
        floatingIndexCode: null,
      })
      setValue(`orderPattern.${index}.exchangeSummaryOrder.deliveryDateRange`, {
        deliveryStartDate: currentBase.deliveryDateRange.deliveryStartDate,
        deliveryEndDate: currentBase.deliveryDateRange.deliveryEndDate,
      })
      return
    }

    if (!spreadTypes.includes(ProductSpreadType.AREA)) {
      setValue(
        `orderPattern.${index}.exchangeSummaryOrder.areaCode`,
        currentBase.areaCode,
      )
    }

    if (!spreadTypes.includes(ProductSpreadType.DELIVERY_PERIOD)) {
      setValue(`orderPattern.${index}.exchangeSummaryOrder`, {
        ...currentExchange,
        deliveryPeriodType: currentBase.deliveryPeriodType,
        eexInstrument: currentBase.eexInstrument,
      })

      setValue(`orderPattern.${index}.exchangeSummaryOrder.deliveryDateRange`, {
        deliveryStartDate: currentBase.deliveryDateRange.deliveryStartDate,
        deliveryEndDate: currentBase.deliveryDateRange.deliveryEndDate,
      })
    }

    if (!spreadTypes.includes(ProductSpreadType.HOUR_TYPE)) {
      setValue(
        `orderPattern.${index}.exchangeSummaryOrder.deliveryHourType`,
        currentBase.deliveryHourType,
      )
      // 交換側の数量は時間帯スプレッドのときのみ入力できるため、解除時は基準側に揃える
      setValue(
        `orderPattern.${index}.exchangeSummaryOrder.volumeInKw`,
        currentBase.volumeInKw,
      )
    } else {
      setValue(
        `orderPattern.${index}.exchangeSummaryOrder.deliveryHourType`,
        currentBase.deliveryHourType === DeliveryHourType.BASE
          ? DeliveryHourType.PEAK
          : DeliveryHourType.BASE,
      )
    }
  }, [isSpreadTrade, spreadTypes, index, getValues, setValue])

  useEffect(() => {
    const currentExchange = getValues(
      `orderPattern.${index}.exchangeSummaryOrder`,
    )
    const currentSpreadType = getValues(`orderPattern.${index}.spreadType`)

    if (currentSpreadType.isSpreadTrade === false) {
      return
    }

    if (
      currentExchange === undefined ||
      Object.keys(currentExchange).length === 0 ||
      currentSpreadType.spreadType === undefined ||
      currentSpreadType.spreadType.length === 0
    ) {
      setValue(`orderPattern.${index}.exchangeSummaryOrder`, {
        ...baseSummaryOrder,
        floatingIndexCode: null,
      })
      return
    }

    if (!currentSpreadType.spreadType.includes(ProductSpreadType.AREA)) {
      setValue(
        `orderPattern.${index}.exchangeSummaryOrder.areaCode`,
        baseSummaryOrder.areaCode,
      )
    }

    if (
      !currentSpreadType.spreadType.includes(
        ProductSpreadType.DELIVERY_PERIOD,
      ) &&
      baseSummaryOrder.deliveryPeriodType !== currentExchange.deliveryPeriodType
    ) {
      setValue(
        `orderPattern.${index}.exchangeSummaryOrder.deliveryPeriodType`,
        baseSummaryOrder.deliveryPeriodType,
      )
    }

    if (
      !currentSpreadType.spreadType.includes(
        ProductSpreadType.DELIVERY_PERIOD,
      ) &&
      (baseSummaryOrder.deliveryDateRange.deliveryStartDate !==
        currentExchange.deliveryDateRange.deliveryStartDate ||
        baseSummaryOrder.deliveryDateRange.deliveryEndDate !==
          currentExchange.deliveryDateRange.deliveryEndDate)
    ) {
      setValue(`orderPattern.${index}.exchangeSummaryOrder`, {
        ...currentExchange,
        eexInstrument: baseSummaryOrder.eexInstrument,
      })

      setValue(`orderPattern.${index}.exchangeSummaryOrder.deliveryDateRange`, {
        deliveryStartDate: baseSummaryOrder.deliveryDateRange.deliveryStartDate,
        deliveryEndDate: baseSummaryOrder.deliveryDateRange.deliveryEndDate,
      })
    }

    if (!currentSpreadType.spreadType.includes(ProductSpreadType.HOUR_TYPE)) {
      if (
        baseSummaryOrder.deliveryHourType !== currentExchange.deliveryHourType
      ) {
        setValue(
          `orderPattern.${index}.exchangeSummaryOrder.deliveryHourType`,
          baseSummaryOrder.deliveryHourType,
        )
      }
    } else {
      // ref: https://www.notion.so/enechain/eClear-EEX-1c231f880991803e894eda80b6b861cc?source=copy_link#1fc31f8809918038b2f2ce16744ab79b
      setValue(
        `orderPattern.${index}.exchangeSummaryOrder.deliveryHourType`,
        baseSummaryOrder.deliveryHourType === DeliveryHourType.BASE
          ? DeliveryHourType.PEAK
          : DeliveryHourType.BASE,
      )
    }

    // 時間帯スプレッドのときは交換側の数量を個別に入力できるため、基準側の値で上書きしない
    if (
      !currentSpreadType.spreadType.includes(ProductSpreadType.HOUR_TYPE) &&
      baseSummaryOrder.volumeInKw !== currentExchange.volumeInKw
    ) {
      setValue(
        `orderPattern.${index}.exchangeSummaryOrder.volumeInKw`,
        baseSummaryOrder.volumeInKw,
      )
    }
  }, [baseSummaryOrder, index, getValues, setValue])

  // 交換側の単価は手入力できるため、基準側の単価か差額単価が実際に変わったときだけ再計算する
  const previousUnitPriceInputRef = useRef({
    baseUnitPricePerKwh,
    diffUnitPrice,
  })

  useEffect(() => {
    const previousUnitPriceInput = previousUnitPriceInputRef.current
    previousUnitPriceInputRef.current = { baseUnitPricePerKwh, diffUnitPrice }

    if (
      previousUnitPriceInput.baseUnitPricePerKwh === baseUnitPricePerKwh &&
      previousUnitPriceInput.diffUnitPrice === diffUnitPrice
    ) {
      return
    }

    const currentSpreadType = getValues(`orderPattern.${index}.spreadType`)
    const currentExchange = getValues(
      `orderPattern.${index}.exchangeSummaryOrder`,
    )

    if (
      currentSpreadType.isSpreadTrade === false ||
      currentExchange === undefined ||
      Object.keys(currentExchange).length === 0
    ) {
      return
    }

    if (
      diffUnitPrice !== undefined &&
      baseUnitPricePerKwh !== undefined &&
      stringUtils.isNotEmpty(diffUnitPrice) &&
      stringUtils.isNotEmpty(baseUnitPricePerKwh) &&
      !isNaN(Number(diffUnitPrice)) &&
      !isNaN(Number(baseUnitPricePerKwh))
    ) {
      setValue(
        `orderPattern.${index}.exchangeSummaryOrder.unitPricePerKwh`,
        new Decimal(diffUnitPrice)
          .add(new Decimal(baseUnitPricePerKwh))
          .toString(),
      )
    }
  }, [baseUnitPricePerKwh, diffUnitPrice, index, getValues, setValue])
}
